import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import _ from "lodash";
import { Form } from "formik";
import { connect, useSelector } from "react-redux";
import Recaptcha from "react-recaptcha";

import { ConsentDisclaimer } from "@/components/Consent/ConsentDisclaimer";
import { OPERATIONAL_OVERRIDE_RECAPTCHA } from "@/constants/featureFlags";
import { createAcknowledgement } from "@/utils/acknowledgementUtils";
import { createImplicitConsent } from "@/utils/consentUtils";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { getCurrentPolicyVersion } from "@/lib/helpers/getCurrentPolicyVersion";

import Button from "../../../UI/Button";
import FormikInput from "../../../UI/FormikInput";
import FormikInputFormat from "../../../UI/formikMobileInput";
import InputError from "../../../UI/InputError";
import {
  adultDobValidationSchemaFactory,
  yup_password_factory,
} from "../../../../utils/yupSchemaUtils";
import images from "../../../../utils/images";
import InterestInGuardianship, {
  GUARDIANSHIP_PROGRAM_TYPE,
} from "../../../Common/InterestInGuardianship";
import { ConsentEmail, ConsentSMS, CustomForm } from "../../../../component-library";
import { ConsentToTreatment } from "@/components/Consent/ConsentToTreatment";
import publicRequests from "../../../../APIRequests/public";
import { Link2 } from "@/ui2/atoms/Link";

const PrimaryAccountInfo = (props) => {
  const { accountData, setAccountData, nextStep } = props;

  let reCaptchaRef = React.createRef();

  const [mounted, setMounted] = useState(false);
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(null);
  const [interestedInGuardianship, setInterestedInGuardianship] = useState(false);
  const [consent, setConsent] = useState({
    email: null,
    sms: null,
    treatment: null,
  });
  const [policyMetadata, setPolicyMetadata] = useState({});
  const publicFlags = useSelector((state) => state.flags.publicFlags);
  const publicFlagsHaveLoaded = useSelector((state) => state.flags.publicFlagsHaveLoaded);
  const policyVersion = getCurrentPolicyVersion({
    policyMetadata,
    policy: "CONSENT_TO_TREATMENT",
  });

  useEffect(() => {
    (async () => {
      setPolicyMetadata(await publicRequests.get_policy_metadata());
    })();
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setMounted(true);
    }, 500);

    if (publicFlags?.[OPERATIONAL_OVERRIDE_RECAPTCHA]) {
      setRecaptchaVerified(true);
    }
  }, [publicFlagsHaveLoaded]);

  const submitHandler = async (formData) => {
    if (!recaptchaVerified) {
      setRecaptchaError("reCAPTCHA is Required");

      return;
    }

    formData.interested_in_guardianship = interestedInGuardianship;

    if (interestedInGuardianship && !formData.guardianship_program_type) {
      formData.guardianship_program_type = GUARDIANSHIP_PROGRAM_TYPE.DEFAULT;
    }

    const implicitConsents = await createImplicitConsent(policyMetadata);

    formData["consent"] = { ...implicitConsents, ...consent };

    setAccountData(formData);
    nextStep();
  };

  const consentHandler = async (policyName, policyVersion) => {
    let currConsent = _.cloneDeep(consent);
    const newConsent = {
      ...(await createAcknowledgement({ consentResponse: true })),
      version: policyVersion,
    };

    if (consent[policyName] == null) {
      currConsent[policyName] = [newConsent];
      setConsent(currConsent);
    } else {
      currConsent[policyName] = null;
      setConsent(currConsent);
    }
  };

  const renderForm = (formikProps) => {
    const formikInputProps = {
      inputClasses: "Input--sm",
      errors: formikProps.errors,
      touched: formikProps.touched,
    };

    return (
      <Form id="selfsignupformik">
        <div className="PrimaryAccountInfo--form-container">
          <FormikInput
            {...formikInputProps}
            formEl={{
              name: "first_name",
              elementType: "input",
              elementConfig: {
                type: "text",
                label: "First Name",
                placeholder: "Enter First Name",
                labelClasses: "fs-16",
              },
            }}
            testId="self-sign-up-first-name"
          />

          <FormikInput
            {...formikInputProps}
            formEl={{
              name: "last_name",
              elementType: "input",
              elementConfig: {
                type: "text",
                label: "Last Name",
                placeholder: "Enter Last Name",
                labelClasses: "fs-16",
              },
            }}
            testId="self-sign-up-last-name"
          />

          <FormikInputFormat
            {...formikInputProps}
            formEl={{
              name: "dob",
              elementType: "input",
              elementConfig: {
                type: "text",
                label: "Date of Birth",
                placeholder: "MM/DD/YYYY",
                labelClasses: "fs-16",
              },
            }}
            value={formikProps && formikProps.values["dob"]}
            onChange={async (val) => {
              await formikProps.setFieldValue("dob", val.value);
              formikProps.setFieldTouched("dob");
            }}
            testId="self-sign-up-dob"
          />

          <FormikInputFormat
            {...formikInputProps}
            formEl={{
              name: "mobile",
              elementType: "input",
              elementConfig: {
                type: "text",
                label: "Mobile",
                placeholder: "Enter Mobile",
                labelClasses: "fs-16",
              },
            }}
            value={formikProps && formikProps.values["mobile"]}
            onChange={async (val) => {
              await formikProps.setFieldValue("mobile", val.value);
              formikProps.setFieldTouched("mobile");
            }}
            testId="self-sign-up-mobile"
          />

          <FormikInput
            {...formikInputProps}
            formEl={{
              name: "email",
              elementType: "input",
              elementConfig: {
                type: "email",
                label: "Email",
                placeholder: "Enter Email",
                labelClasses: "fs-16",
              },
            }}
            testId="self-sign-up-email"
          />

          <FormikInput
            {...formikInputProps}
            formEl={{
              name: "password",
              elementType: "input",
              elementConfig: {
                type: "viewablePassword",
                label: "Create Password",
                placeholder: "Enter Password",
                labelClasses: "fs-16",
              },
            }}
            testId="self-sign-up-password"
          />

          <FormikInput
            {...formikInputProps}
            formEl={{
              name: "confirm_password",
              elementType: "input",
              elementConfig: {
                type: "viewablePassword",
                label: "Confirm Password",
                placeholder: "Re-enter Password",
                labelClasses: "fs-16",
              },
            }}
            testId="self-sign-up-confirm-password"
          />

          <div className="my-3 d-flex fs-15">
            <InterestInGuardianship
              interestedInGuardianship={interestedInGuardianship}
              setInterestedInGuardianship={() =>
                setInterestedInGuardianship(!interestedInGuardianship)
              }
              testId="self-sign-up-interested-in-guardianship"
            />
          </div>
        </div>
      </Form>
    );
  };

  return (
    <>
      <div className="p-5">
        <div className="fs-24 text-center tw:font-bold">Welcome to aptihealth!</div>
        <div className="fs-18 text-center txt-gry">Let’s get started by creating your account</div>

        <div className="text-center mt-3">
          <span>Already have an account?</span>
          <Link2 className="tw:ml-ch" href="/auth/sign-in">
            <b>Log In</b>
          </Link2>
        </div>

        <div className="text-center my-4">
          <img
            src={images("./signup/progress-1.png")}
            alt="Step 1/3"
            className="signup-progress-bar"
          />
        </div>

        <CustomForm
          initialValues={accountData}
          validationSchema={accountPart1Validation()}
          onSubmit={submitHandler}
          render={renderForm}
        />

        <div>
          <ConsentToTreatment
            className="tw:my-0 tw:bg-fill-white tw:pb-0 tw:pl-0 tw:[&>p]:!text-sm"
            consentEvent={() => consentHandler("treatment", policyVersion)}
            version={policyVersion}
          />
        </div>

        <div className="tw:my-3 tw:text-sm tw:text-text-tertiary">
          <span id="self-signup-consent">
            <ConsentEmail consentEvent={() => consentHandler("email")} />
          </span>
        </div>

        <div className="tw:my-3 tw:text-sm tw:text-text-tertiary">
          <span id="self-signup-consent">
            <ConsentSMS consentEvent={() => consentHandler("sms")} />
          </span>
        </div>

        <div className="mt-5 mb-4 text-center w-100">
          <div>
            {mounted && (
              <Recaptcha
                className="mx-auto recaptcha"
                size={window.innerWidth < 768 ? "compact" : "normal"}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                ref={reCaptchaRef}
                verifyCallback={() => {
                  setRecaptchaError(null);
                  setRecaptchaVerified(true);
                }}
              />
            )}
          </div>

          <div className="text-center my-2">
            {recaptchaError && (
              <InputError style={{ position: "relative" }}>{recaptchaError}</InputError>
            )}
          </div>
        </div>

        <div className="d-flex tw:justify-center">
          <Button
            form="selfsignupformik"
            type="submit"
            className="Btn Btn--pri self-signup-btn"
            disabled={consent["treatment"] ? null : "disabled"}
            {...getAutomationDataAttr("self-sign-up-primary-info-button")}
          >
            Agree & Continue
          </Button>
        </div>

        <ConsentDisclaimer className="tw:!mt-8" policyMetadata={policyMetadata} />
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    configs: state.configs,
  };
};

export default connect(mapStateToProps, {})(PrimaryAccountInfo);

const accountPart1Validation = () =>
  Yup.object().shape({
    first_name: Yup.string()
      .required("First name is required")
      .matches(/^[a-zA-Z-]+$/, "Numerals, spaces & special characters are not allowed"),
    last_name: Yup.string()
      .required("Last name is required")
      .matches(/^[a-zA-Z-]+$/, "Numerals, spaces & special characters are not allowed"),
    dob: adultDobValidationSchemaFactory(
      "MMDDYYYY",
      "Must be a valid date",
      "To register a child for care aged 5-17, you must first register yourself. " +
        "Toggle the button below and you will be prompted to register your child when you finish.",
    ).required("Date of birth is required"),
    mobile: Yup.string()
      .matches(/^\d{10}$/, "Contact number must be a valid US number")
      .required("Mobile number is required"),
    email: Yup.string().email("Enter a valid email address").required("Email is required"),
    password: yup_password_factory(),
    confirm_password: Yup.string()
      .required("Confirm password is required")
      .oneOf([Yup.ref("password"), null], "Password does not match"),
  });
